import type React from "react";
import { useRef, useState } from "react";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import {
  A11y,
  Autoplay,
  Navigation,
  Pagination,
  Scrollbar,
} from "swiper/modules";
import {
  Swiper,
  type SwiperClass,
  type SwiperProps,
  type SwiperRef,
  SwiperSlide,
} from "swiper/react";

import { locale } from "@/lib/i18n";

import "swiper/css";

import ProgressPagination from "./ProgressPagination";

type Props = {
  slides: React.ReactNode[];
  pagination?: boolean;
  scrollbar?: boolean;
  paginationColor?: string;
  autoplay?: boolean;
  autoplayDelay?: number;
  useProgressPagination?: boolean;
} & SwiperProps;

export default function Carousel({
  slides,
  pagination = true,
  scrollbar = true,
  paginationColor = "#7F56D9",
  autoplay = false,
  autoplayDelay = 3000,
  useProgressPagination,
  ...rest
}: Props) {
  const swiperRef = useRef<SwiperRef>(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(
    null,
  );

  const shouldUseProgressPagination =
    useProgressPagination !== undefined
      ? useProgressPagination
      : autoplay && pagination;

  return (
    <div
      style={{
        ["--swiper-theme-color" as any]: paginationColor,
        ["--swiper-pagination-color" as any]: paginationColor,
      }}
    >
      <Swiper
        key={locale.isRtl ? "rtl" : "ltr"}
        dir={locale.isRtl ? "rtl" : "ltr"}
        ref={swiperRef}
        onSwiper={setSwiperInstance}
        modules={
          [
            (shouldUseProgressPagination || rest.navigation) && Navigation,
            !shouldUseProgressPagination && pagination && Pagination,
            scrollbar && Scrollbar,
            autoplay && Autoplay,
            A11y,
          ].filter(Boolean) as Props["modules"]
        }
        pagination={
          shouldUseProgressPagination
            ? false
            : pagination
              ? { clickable: true }
              : false
        }
        scrollbar={scrollbar ? { draggable: true } : false}
        autoplay={autoplay ? { delay: autoplayDelay } : false}
        navigation={
          shouldUseProgressPagination
            ? {
                prevEl: ".swiper-button-prev-custom",
                nextEl: ".swiper-button-next-custom",
              }
            : rest.navigation
        }
        className="pb-13!"
        {...rest}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>{slide}</SwiperSlide>
        ))}
      </Swiper>

      {shouldUseProgressPagination && (
        <ProgressPagination
          swiper={swiperInstance}
          totalSlides={slides.length}
          autoplayDelay={autoplayDelay}
          onPrev={() => swiperRef.current?.swiper.slidePrev()}
          onNext={() => swiperRef.current?.swiper.slideNext()}
        />
      )}
    </div>
  );
}
